// Controls for choosing the 2-D slice through an N-D campaign input space.
// - Two Radix Selects pick the X and Y axes.
// - One SliderRow per remaining range-mode input, pinning its value.
// - A slider sets the slab fraction (0..50%).
// - Fixed-mode inputs are rendered as read-only badges.
//
// Hidden entirely when the campaign has only two range-mode inputs and no
// fixed inputs (there is nothing meaningful to configure).

import { Badge, Flex, Select, Text } from '@radix-ui/themes';
import type { ReactElement } from 'react';

import { SliderRow } from './_primitiveBridge';
import type { AxisSpec, SliceState } from '../types';

export type SliceControlsProps = {
  inputs: readonly AxisSpec[];
  slice: SliceState;
  onChange: (next: SliceState) => void;
};

export function SliceControls(props: SliceControlsProps): ReactElement | null {
  const { inputs, slice, onChange } = props;

  const rangeInputs = inputs.filter((a) => a.mode === 'range');
  const fixedInputs = inputs.filter((a) => a.mode === 'fixed');

  // Render nothing when the campaign is purely 2-D with no fixed axes.
  if (rangeInputs.length <= 2 && fixedInputs.length === 0) {
    return null;
  }

  // Non-(x,y) range inputs are the ones the user pins.
  const pinnedAxes = rangeInputs.filter(
    (a) => a.name !== slice.xInput && a.name !== slice.yInput,
  );

  const renderAxisSelect = (
    label: string,
    value: string,
    onSelect: (next: string) => void,
  ): ReactElement => (
    <Flex align="center" gap="2">
      <Text size="1" color="gray" style={{ textTransform: 'uppercase' }}>
        {label}
      </Text>
      <Select.Root value={value} onValueChange={onSelect}>
        <Select.Trigger aria-label={label} />
        <Select.Content position="popper">
          {rangeInputs.map((axis) => (
            <Select.Item key={axis.name} value={axis.name}>
              {`${axis.name} (${axis.units})`}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </Flex>
  );

  const handleXChange = (next: string) => {
    if (next === slice.xInput) return;
    // If the chosen axis collides with Y, swap them.
    let yInput = slice.yInput;
    if (next === yInput) yInput = slice.xInput;
    onChange({ ...slice, xInput: next, yInput });
  };

  const handleYChange = (next: string) => {
    if (next === slice.yInput) return;
    let xInput = slice.xInput;
    if (next === xInput) xInput = slice.yInput;
    onChange({ ...slice, xInput, yInput: next });
  };

  const handlePinnedChange = (axis: AxisSpec, value: number) => {
    onChange({
      ...slice,
      pinned: { ...slice.pinned, [axis.name]: value },
    });
  };

  const handleSlabChange = (next: number) => {
    const clamped = Math.max(0, Math.min(0.5, next));
    onChange({ ...slice, slabFraction: clamped });
  };

  return (
    <Flex
      direction="column"
      gap="2"
      p="3"
      style={{
        background: '#0d0f12',
        border: '1px solid #1f2328',
        borderRadius: 6,
      }}
      data-testid="slice-controls"
    >
      {rangeInputs.length > 2 && (
        <Flex gap="4" wrap="wrap">
          {renderAxisSelect('X axis', slice.xInput, handleXChange)}
          {renderAxisSelect('Y axis', slice.yInput, handleYChange)}
        </Flex>
      )}

      {pinnedAxes.map((axis) => (
        <SliderRow
          key={axis.name}
          name={axis.name}
          min={axis.min ?? 0}
          max={axis.max ?? 1}
          value={slice.pinned[axis.name] ?? 0}
          units={axis.units}
          onChange={(v: number) => handlePinnedChange(axis, v)}
        />
      ))}

      {rangeInputs.length > 2 && (
        <SliderRow
          name="Slab thickness"
          min={0}
          max={0.5}
          value={slice.slabFraction}
          units="fraction"
          onChange={handleSlabChange}
        />
      )}

      {fixedInputs.length > 0 && (
        <Flex gap="2" wrap="wrap">
          {fixedInputs.map((axis) => (
            <Badge key={axis.name} variant="soft" color="gray" data-testid="fixed-badge">
              {`${axis.name} = ${axis.fixedValue ?? 0}${
                axis.units && axis.units !== 'dimensionless' ? ' ' + axis.units : ''
              } (fixed)`}
            </Badge>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
